import { Box } from "@mui/system";
import { DateValidationError } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import { BaseFieldProps } from "./FieldProps";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import { PRODUCT_STATUS } from "../../models/Product";
import { useFormStore } from "../../stores/formStore";
import CalendarIcon from "../icons/ui-actions/CalendarIcon";

type Props<TFieldValues extends FieldValues> = {
  dateNotBefore?: Path<TFieldValues>;
  disablePast?: boolean;
  maxDate?: boolean;
  onCustomChange?: (date: Dayjs | null) => void
} & BaseFieldProps<TFieldValues>;

const DateField = <TFieldValues extends FieldValues>({
  name,
  label,
  disabled = false,
  disabledForEdit = false,
  dateNotBefore,
  disablePast = false,
  maxDate = false,
  onCustomChange,
}: Props<TFieldValues>) => {
  const [minDate, setMinDate] = useState<Dayjs | undefined>(undefined);
  const [dateError, setDateError] = useState<DateValidationError>(null);

  const { control, clearErrors, watch, setError } =
    useFormContext<TFieldValues>();
  const { field } = useController({
    name,
    control,
  });
  const startDateSelected = dateNotBefore && watch(dateNotBefore);
  const status = useFormStore(state => state.status);

  const errorMessage: Record<string, string> = {
    minDate: "Start date must be before End date",
    disablePast: "This date cannot be in the past",
    invalidDate: "Your date is not valid",
  };

  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);

  const handleChange = (date: Dayjs | null) => {
    const formDate = date?.format("DD/MM/YYYY HH:mm:ss");
    field.onChange(
      formDate as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    );

    // Call onCustomChange if provided
    if (onCustomChange) {
      onCustomChange(date);
    }
  };

  useEffect(() => {
    if (dateNotBefore) {
      setMinDate(dayjs(startDateSelected, "DD/MM/YYYY HH:mm:ss"));
    }
    if (!dateError) {
      clearErrors(field.name);
    } else {
      setError(field.name, {
        message: errorMessage[dateError],
      });
    }
  }, [startDateSelected, dateError]);

  return (
    <Box data-testid={`form-${label}-DATE`} width={"-webkit-fill-available"}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...field}
          value={field.value ? dayjs(field.value, "DD/MM/YYYY HH:mm:ss") : null}
          onChange={handleChange}
          disabled={fieldIsDisabled}
          label={label}
          views={["year", "month", "day"]}
          format="DD/MM/YYYY"
          slots={{
            openPickerIcon: CalendarIcon,
          }}
          onError={newError => setDateError(newError)}
          slotProps={{
            textField: {
              helperText: dateError && errorMessage[dateError],
            },
          }}
          disablePast={
            disablePast &&
            [PRODUCT_STATUS.DRAFT, PRODUCT_STATUS.PENDING_APPROVAL].includes(
              status || PRODUCT_STATUS.DRAFT,
            )
          }
          minDate={minDate}
          maxDate={maxDate ? dayjs() : undefined}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DateField;
